import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Slider } from '../components/ui/slider';
import api from '../utils/api';
import { toast } from 'sonner';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { DictationButton } from '../components/DictationButton';

export const Assessment = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [step, setStep] = useState(0); // 0: core, 1: meta, 2: written
  const [loading, setLoading] = useState(false);

  // Scroll to top on mount and step change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);
  
  const [scores, setScores] = useState({
    physical_health: 5,
    strength_fitness: 5,
    energy: 5,
    environment_order: 5,
    work_mission: 5,
    money_financial: 5,
    love_relationship: 5,
    friends_community: 5,
    emotional_stability: 5,
    discipline_consistency: 5,
    learning_growth: 5,
    meaning_purpose: 5,
  });

  const [metaScores, setMetaScores] = useState({
    direction: 5,
    alignment: 5,
    momentum: 5,
    stress_overwhelm: 5,
  });

  const [writtenAnswers, setWrittenAnswers] = useState({
    building_achieving: '',
    biggest_problem: '',
    most_stuck: '',
    going_well: '',
    avoiding: '',
    one_year_different: '',
    anything_else: '',
  });

  const QUESTIONS = {
    core: [
      { key: 'physical_health', label: t('assessment.physicalHealth'), question: t('assessment.physicalHealthQ') },
      { key: 'strength_fitness', label: t('assessment.strengthFitness'), question: t('assessment.strengthFitnessQ') },
      { key: 'energy', label: t('assessment.energy'), question: t('assessment.energyQ') },
      { key: 'environment_order', label: t('assessment.environmentOrder'), question: t('assessment.environmentOrderQ') },
      { key: 'work_mission', label: t('assessment.workMission'), question: t('assessment.workMissionQ') },
      { key: 'money_financial', label: t('assessment.moneyFinancial'), question: t('assessment.moneyFinancialQ') },
      { key: 'love_relationship', label: t('assessment.loveRelationship'), question: t('assessment.loveRelationshipQ') },
      { key: 'friends_community', label: t('assessment.friendsCommunity'), question: t('assessment.friendsCommunityQ') },
      { key: 'emotional_stability', label: t('assessment.emotionalStability'), question: t('assessment.emotionalStabilityQ') },
      { key: 'discipline_consistency', label: t('assessment.disciplineConsistency'), question: t('assessment.disciplineConsistencyQ') },
      { key: 'learning_growth', label: t('assessment.learningGrowth'), question: t('assessment.learningGrowthQ') },
      { key: 'meaning_purpose', label: t('assessment.meaningPurpose'), question: t('assessment.meaningPurposeQ') },
    ],
    meta: [
      { key: 'direction', label: t('assessment.direction'), question: t('assessment.directionQ') },
      { key: 'alignment', label: t('assessment.alignment'), question: t('assessment.alignmentQ') },
      { key: 'momentum', label: t('assessment.momentum'), question: t('assessment.momentumQ') },
      { key: 'stress_overwhelm', label: t('assessment.stressOverwhelm'), question: t('assessment.stressOverwhelmQ') },
    ],
    written: [
      { key: 'going_well', question: t('assessment.goingWell') },
      { key: 'building_achieving', question: t('assessment.buildingAchieving') },
      { key: 'biggest_problem', question: t('assessment.biggestProblem') },
      { key: 'most_stuck', question: t('assessment.mostStuck') },
      { key: 'avoiding', question: t('assessment.avoiding') },
      { key: 'one_year_different', question: t('assessment.oneYearDifferent') },
      { key: 'anything_else', question: t('assessment.anythingElse') },
    ],
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await api.post('/assessment', {
        scores,
        meta_scores: metaScores,
        written_answers: writtenAnswers,
        assessment_type: 'initial',
      });
      toast.success(t('assessment.success'));
      navigate('/map-reveal');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('assessment.failed'));
    } finally {
      setLoading(false);
    }
  };

  const renderCoreQuestions = () => (
    <div className="space-y-12" data-testid="core-questions-section">
      <div className="text-center mb-12">
        <h2 className="font-serif font-light text-4xl text-[#F8F5F2] mb-4">
          {t('assessment.title')}
        </h2>
        <p className="font-sans font-light text-[#D1D1D6] text-lg">
          {t('assessment.subtitle')}
        </p>
      </div>

      {QUESTIONS.core.map((q, index) => (
        <motion.div
          key={q.key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="glass-panel rounded-xl p-6"
          data-testid={`question-${q.key}`}
        >
          <div className="mb-4">
            <p className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-2">
              {q.label}
            </p>
            <p className="font-sans font-light text-[#F8F5F2] text-lg">
              {q.question}
            </p>
          </div>
          
          {/* Rating instruction - only show on first question */}
          {index === 0 && (
            <p className="text-[#A1A1AA] text-xs font-sans mb-3 text-center animate-pulse">
              👆 {language === 'tr' ? 'Puanınızı seçmek için kaydırın veya dokunun' : 'Drag or tap to select your rating'}
            </p>
          )}
          
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center w-10">
              <span className="text-[#A1A1AA] font-sans text-sm">1</span>
              <span className="text-[#6B7280] font-sans text-[10px]">{language === 'tr' ? 'Düşük' : 'Low'}</span>
            </div>
            <Slider
              value={[scores[q.key]]}
              onValueChange={(value) => setScores({ ...scores, [q.key]: value[0] })}
              min={1}
              max={10}
              step={1}
              className="flex-1"
              data-testid={`slider-${q.key}`}
            />
            <div className="flex flex-col items-center w-10">
              <span className="text-[#A1A1AA] font-sans text-sm">10</span>
              <span className="text-[#6B7280] font-sans text-[10px]">{language === 'tr' ? 'Yüksek' : 'High'}</span>
            </div>
            <span className="text-[#D4AF37] font-sans font-semibold text-2xl w-12 text-right">
              {scores[q.key]}
            </span>
          </div>
        </motion.div>
      ))}

      <div className="flex flex-col items-center pt-8">
        <button
          onClick={() => setStep(1)}
          className="rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-12 py-4 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.15)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] font-sans text-lg flex items-center gap-2"
          data-testid="continue-to-meta-button"
        >
          {t('assessment.continue')} <ChevronRight className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => navigate('/orbit')}
          className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm mt-4 py-2"
        >
          {language === 'tr' ? 'Şimdilik atla' : 'Skip for now'}
        </button>
      </div>
    </div>
  );

  const renderMetaQuestions = () => (
    <div className="space-y-12" data-testid="meta-questions-section">
      <div className="text-center mb-12">
        <h2 className="font-serif font-light text-4xl text-[#F8F5F2] mb-4">
          {t('assessment.metaTitle')}
        </h2>
        <p className="font-sans font-light text-[#D1D1D6] text-lg">
          {t('assessment.metaSubtitle')}
        </p>
      </div>

      {QUESTIONS.meta.map((q, index) => (
        <motion.div
          key={q.key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="glass-panel rounded-xl p-6"
          data-testid={`meta-question-${q.key}`}
        >
          <div className="mb-4">
            <p className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-2">
              {q.label}
            </p>
            <p className="font-sans font-light text-[#F8F5F2] text-lg">
              {q.question}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center w-10">
              <span className="text-[#A1A1AA] font-sans text-sm">1</span>
              <span className="text-[#6B7280] font-sans text-[10px]">{language === 'tr' ? 'Düşük' : 'Low'}</span>
            </div>
            <Slider
              value={[metaScores[q.key]]}
              onValueChange={(value) => setMetaScores({ ...metaScores, [q.key]: value[0] })}
              min={1}
              max={10}
              step={1}
              className="flex-1"
              data-testid={`meta-slider-${q.key}`}
            />
            <div className="flex flex-col items-center w-10">
              <span className="text-[#A1A1AA] font-sans text-sm">10</span>
              <span className="text-[#6B7280] font-sans text-[10px]">{language === 'tr' ? 'Yüksek' : 'High'}</span>
            </div>
            <span className="text-[#D4AF37] font-sans font-semibold text-2xl w-12 text-right">
              {metaScores[q.key]}
            </span>
          </div>
        </motion.div>
      ))}

      <div className="flex flex-col items-center pt-8">
        <div className="flex justify-center gap-4">
          <button
            onClick={() => setStep(0)}
            className="text-white/70 hover:text-white transition-colors duration-300 uppercase tracking-widest text-sm font-sans px-8 py-4"
            data-testid="back-to-core-button"
          >
            {t('assessment.back')}
          </button>
          <button
            onClick={() => setStep(2)}
            className="rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-12 py-4 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.15)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] font-sans text-lg flex items-center gap-2"
            data-testid="continue-to-written-button"
          >
            {t('assessment.continue')} <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/orbit')}
          className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm mt-4 py-2"
        >
          {language === 'tr' ? 'Şimdilik atla' : 'Skip for now'}
        </button>
      </div>
    </div>
  );

  const renderWrittenQuestions = () => (
    <div className="space-y-8" data-testid="written-questions-section">
      <div className="text-center mb-12">
        <h2 className="font-serif font-light text-4xl text-[#F8F5F2] mb-4">
          {t('assessment.writtenTitle')}
        </h2>
        <p className="font-sans font-light text-[#D1D1D6] text-lg">
          {t('assessment.writtenSubtitle')}
        </p>
      </div>

      {QUESTIONS.written.map((q, index) => (
        <motion.div
          key={q.key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="glass-panel rounded-xl p-6"
          data-testid={`written-question-${q.key}`}
        >
          <label className="font-sans font-light text-[#F8F5F2] text-lg block mb-3">
            {q.question}
          </label>
          <div className="relative">
            <textarea
              value={writtenAnswers[q.key]}
              onChange={(e) => setWrittenAnswers({ ...writtenAnswers, [q.key]: e.target.value })}
              className="w-full bg-white/5 border border-[#D4AF37]/15 rounded-lg px-4 py-3 pr-14 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none transition-all min-h-[100px] resize-y"
              placeholder={t('assessment.sharePlaceholder')}
              data-testid={`textarea-${q.key}`}
            />
            <DictationButton
              onTranscript={(text) => setWrittenAnswers({ ...writtenAnswers, [q.key]: writtenAnswers[q.key] + (writtenAnswers[q.key] ? ' ' : '') + text })}
              className="absolute right-3 top-3"
            />
          </div>
        </motion.div>
      ))}

      <div className="flex flex-col items-center pt-8">
        <div className="flex justify-center gap-4">
          <button
            onClick={() => setStep(1)}
            className="text-white/70 hover:text-white transition-colors duration-300 uppercase tracking-widest text-sm font-sans px-8 py-4"
            data-testid="back-to-meta-button"
          >
            {t('assessment.back')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-12 py-4 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.15)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] font-sans text-lg flex items-center gap-2 disabled:opacity-50"
            data-testid="generate-sun-map-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('assessment.generating')}
              </>
            ) : (
              <>
                {t('assessment.generate')} <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/orbit')}
          className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm mt-4 py-2"
        >
          {language === 'tr' ? 'Şimdilik atla' : 'Skip for now'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen relative py-16 px-6">
      <div className="max-w-4xl mx-auto">
        {step === 0 && renderCoreQuestions()}
        {step === 1 && renderMetaQuestions()}
        {step === 2 && renderWrittenQuestions()}
      </div>
    </div>
  );
};

export default Assessment;
